/* istanbul ignore file */
import React from 'react';
import { batch } from 'react-redux';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { parse } from 'url';

import { adaptConfigFacetsToStore } from 'src/adapters/configToStore/listings-api/adaptConfigFacetsToStore';
import { adaptConfigSortToStore } from 'src/adapters/configToStore/listings-api/adaptConfigSortToStore';
import type { SearchListingProps } from 'src/components/layouts/SearchListing/SearchListing';
import { SearchListingContainer } from 'src/components/layouts/SearchListing/SearchListingContainer';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore } from 'src/redux/store';
import { actions as userActions } from 'src/redux/user/slice';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { getSearchServingConfig } from 'src/services/listings-api/servingConfigs';
import { getServerSidePageEventObject } from 'src/tracking/events/pageEvent';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import { getPageFromParam } from 'src/utils/queryParams';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

export const getServerSideProps: GetServerSideProps = async ({ query, req, res }) => {
  const featureFlags = (req && getFeatureFlagsWithMatchers(req)) || {};
  const { term, ...queryParams } = query;
  const page = getPageFromParam(query);
  const searchTerm = (Array.isArray(term) ? term[0] : term) || '';
  const pathname = parse(req.url!).pathname!;
  const { _ga: googleAnalyticsVisitorId, cognitoId, currency, delivery_zone } = req.cookies;
  const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
    parseLocaleSetCookieHeaders(res.getHeaders());
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);
  const facets = adaptConfigFacetsToStore(query);
  const sortOptions = adaptConfigSortToStore(query);

  if (!searchTerm) {
    return {
      redirect: {
        destination: publicRuntimeConfig.hosts.search,
        statusCode: StatusCode.MovedPermanently,
      },
    };
  }

  const servingConfig = getSearchServingConfig(featureFlags);

  const { dispatch, getState } = getStore(
    {
      router: { pathname, query: queryParams },
    },
    {
      navigation: {
        pageType: PageType.Search,
      },
      productsQuery: {
        facets,
        page,
        searchTerm,
        ...(servingConfig && { servingConfig }),
        sortOptions,
      },
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        cognitoID: cognitoId || null,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
    dispatch(userActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
    dispatch(userActions.setFeatureFlags(featureFlags));
  });

  const urlPath = req.url;
  const [, [navData, topBannerData]] = await Promise.all([
    dispatch(requestProducts({})),
    fetchGlobalContent({ featureFlags, urlPath }),
  ]);

  const state = getState();
  const { products } = state;

  if (products.error) {
    res.statusCode = StatusCode.InternalServerError;
    res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
  }

  const pageEventData = getServerSidePageEventObject(
    publicRuntimeConfig.version,
    publicRuntimeConfig.appVersion,
    state,
  );

  const props: Partial<SearchListingProps> = {
    featureFlags,
    navData,
    searchTerm,
    topBannerData,
    pageEventData,
    ...selectPagePropsForClientHydration(state),
  };

  return {
    props,
  };
};

const SearchResultsProductsPage = (props: SearchListingProps) => {
  return (
    <>
      <Head>
        <title>{`Search results for ${props.searchTerm}`}</title>
        <meta content="noindex" name="robots" />
      </Head>
      <SearchListingContainer {...props} />
    </>
  );
};

export default SearchResultsProductsPage;
